<script setup lang="ts">
import { GpFadeTransition } from '@gportal/uikit';
import IntroductionSection from '@panel/components/welcome/01.IntroductionSection.vue';
import EmailConfirmSection from '@panel/components/welcome/02.EmailConfirmSection.vue';
import ProjectInvitesSection from '@panel/components/welcome/03.ProjectInvitesSection.vue';
import ProjectCreateSection from '@panel/components/welcome/04.ProjectCreateSection.vue';
import NodeLimitSection from '@panel/components/welcome/05.NodeLimitSection.vue';
import useSetupValidation from '@panel/components/welcome/composables/useSetupValidation';
import useViewTitle from '@panel/composables/useViewTitle';
import LayoutSinglePage from '@panel/layouts/Layout.SinglePage.vue';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import Size from 'gpcore-uikit/interfaces/Size';
import { ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';

useViewTitle('Welcome');

const { userEmailIsConfirmed, userHasProjects } = useSetupValidation();
const getFirstStep = () => {
  // automatically skip steps if user fulfills the requirements
  let step = 0;

  if (userEmailIsConfirmed.value) {
    step = 2;

    if (userHasProjects.value) {
      step = 4;
    }
  }

  return step;
};

const steps = ['welcome', 'email', 'projectInvite', 'projectCreate', 'limit'] as const;
const currentStep = ref<number>(getFirstStep());
const currentStepName = ref<(typeof steps)[number]>(steps[currentStep.value]);
watchEffect(() => {
  currentStepName.value = steps[currentStep.value];
});

const goForwards = (step = 1) => {
  if (currentStep.value === steps.length - 1) {
    return;
  }

  currentStep.value += step;
};

const projetStore = useProjectStore();
const router = useRouter();
const finishSetup = () => {
  router.push({
    name: 'ProjectNodes',
    params: {
      projectId: projetStore.projectId,
    },
  });
};

const slideLeftTransitionClasses = {
  enterFromClass: 'tw-translate-x-[5rem] tw-opacity-0',
  enterActiveClass: 'tw-transition-all tw-transition-ease-in',
  enterToClass: 'tw-translate-x-0 tw-opacity-100',
  leaveFromClass: 'tw-translate-x-0 tw-opacity-100',
  leaveActiveClass: 'tw-transition-all tw-transition-ease-out',
  leaveToClass: '-tw-translate-x-[5rem] tw-opacity-0',
};
</script>

<template>
  <LayoutSinglePage :content-size="Size.Tiny" v-playwright="'welcome-view'">
    <template #content>
      <transition v-bind="slideLeftTransitionClasses" mode="out-in">
        <IntroductionSection
          v-if="currentStepName === 'welcome'"
          key="welcome"
          class="tw-mt-8"
          @next-button-clicked="goForwards"
        />
        <EmailConfirmSection
          v-else-if="currentStepName === 'email'"
          key="email"
          class="tw-mt-8"
          @next-button-clicked="goForwards"
        />
        <ProjectInvitesSection
          v-else-if="currentStepName === 'projectInvite'"
          key="projectInvite"
          class="tw-mt-8"
          @next-button-clicked="goForwards"
          @skip-button-clicked="goForwards(2)"
        />
        <ProjectCreateSection
          v-else-if="currentStepName === 'projectCreate'"
          key="projectCreate"
          class="tw-mt-8"
          @next-button-clicked="goForwards"
        />
        <NodeLimitSection
          v-else-if="currentStepName === 'limit'"
          key="limit"
          class="tw-mt-8"
          @next-button-clicked="finishSetup"
        />
      </transition>

      <!-- indicator -->
      <GpFadeTransition>
        <div
          v-if="currentStep > 0"
          class="tw-mx-auto -tw-mt-4 tw-mb-4 tw-flex tw-justify-center tw-gap-2"
        >
          <span
            v-for="step in steps.length - 1"
            :key="step"
            class="tw-inline tw-text-4xl tw-transition-all"
            :class="{
              'tw-opacity-75': step !== currentStep,
              'tw-text-gp-cyan': step === currentStep,
            }"
          >
            &#x2022;
          </span>
        </div>
      </GpFadeTransition>
    </template>
  </LayoutSinglePage>
</template>

<style scoped></style>
